import { BaseApiClient } from './base-client';
import type { ApiResponse } from '../../types';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function sanitizeTeamId(teamId?: string | null): string | null {
  return teamId && UUID_REGEX.test(teamId) ? teamId : null;
}

/**
 * Gallery API - методы для работы с галереей
 */
export class GalleryApi extends BaseApiClient {
  
  // ===== GALLERY API =====
  
  async getGalleryItems(
    type: 'image' | 'video',
    folderId?: string | null,
    rawTeamId?: string | null,
    limit?: number,
    offset?: number
  ): Promise<any> {
    const teamId = sanitizeTeamId(rawTeamId);
    const params = new URLSearchParams();
    params.append('type', type);
    if (folderId) {
      params.append('folderId', folderId);
    }
    if (teamId) {
      params.append('teamId', teamId);
    }
    if (limit !== undefined) {
      params.append('limit', String(limit));
    }
    if (offset !== undefined && offset > 0) {
      params.append('offset', String(offset));
    }

    const response = await this.client.get(`/api/gallery/items?${params.toString()}`);
    return response.data;
  }

  async deleteGalleryItem(itemId: string): Promise<ApiResponse> {
    const response = await this.client.delete(`/api/gallery/items/${itemId}`);
    return response.data;
  }

  async deleteGalleryItems(itemIds: string[]): Promise<ApiResponse> {
    const response = await this.client.post('/api/gallery/items/delete-bulk', { itemIds });
    return response.data;
  }

  async toggleGalleryItemFavorite(itemId: string, isFavorite: boolean): Promise<ApiResponse> {
    const response = await this.client.patch(`/api/gallery/items/${itemId}/favorite`, { isFavorite });
    return response.data;
  }

  async createGalleryFolder(name: string, type: 'image' | 'video'): Promise<ApiResponse> {
    const response = await this.client.post('/api/gallery/folders', { name, type });
    return response.data;
  }

  async deleteGalleryFolder(folderId: string): Promise<ApiResponse> {
    const response = await this.client.delete(`/api/gallery/folders/${folderId}`);
    return response.data;
  }

  async renameGalleryFolder(folderId: string, name: string): Promise<ApiResponse> {
    const response = await this.client.patch(`/api/gallery/folders/${folderId}`, { name });
    return response.data;
  }

  async moveGalleryItems(itemIds: string[], folderId: string | null, rawTeamId?: string | null): Promise<ApiResponse> {
    const teamId = sanitizeTeamId(rawTeamId);
    const body: any = { itemIds, folderId };
    if (teamId) body.teamId = teamId;
    const response = await this.client.post('/api/gallery/items/move', body);
    return response.data;
  }

  /**
   * Скачать ZIP-архив файлов из папки или по списку ID
   * Возвращает Blob для сохранения в браузере
   */
  async downloadZip(params: { folderId?: string; itemIds?: string[] }): Promise<Blob> {
    const response = await this.client.post('/api/gallery/download-zip', params, {
      responseType: 'blob',
      timeout: 120000 // 2 минуты на большие архивы
    });
    return response.data;
  }
}

